import { httpService } from '../services/httpService';
import { permissionsService } from '../services/permissionsService';
import { log } from '../utils/DebugLogger';
import { validate } from '../contracts/validate';
import '../contracts/OrcamentosServicos_SalvaOrcamentoServicoInput.ts';
import '../contracts/OrcamentosServicos_CarregaOrcamentoServicoResponse.ts';
import '../contracts/OrcamentosServicos_BuscaOrcamentoServicosPorOrcamentoIDResponse';
import '../contracts/OrcamentosServicos_CancelaorcamentoservicoResponse';
import '../contracts/OrcamentosServicos_CarregaOrcamentoServicoResponse';
import '../contracts/OrcamentosServicos_ReativarOrcamentoServicoResponse';
import '../contracts/OrcamentosServicos_RemoveorcamentoservicoResponse';
import '../contracts/OrcamentosServicos_SalvaOrcamentoServicoResponse';
import '../contracts/OrcamentosServicos_SelectserviciosResponse';
import '../contracts/OrcamentosServicos_VerificaprecancelamentopororcamentoservicoidResponse';

import type { OrcamentosServicos_SalvaOrcamentoServicoInput } from '../contracts/OrcamentosServicos_SalvaOrcamentoServicoInput';
export function useOrcamentoServico() {
  
  // Fetch orcamento servicos by orcamento ID
  const fetchOrcamentoServicos = async (orcamentoId: number) => {
    log('useOrcamentoServico', 'fetchOrcamentoServicos', 'Fetching servicos for orcamento', orcamentoId);

    try {
      const response = await httpService.get(
        `/index.php/orcamentos_servicos/buscaOrcamentoServicosPorOrcamentoID/${orcamentoId}`,
        { responseContract: 'OrcamentosServicos_BuscaOrcamentoServicosPorOrcamentoIDResponse' }
      );

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar serviços do orçamento');
      }
    } catch (error) {
      log('useOrcamentoServico', 'fetchOrcamentoServicos', 'Error', error);
      throw error;
    }
  };

  // Fetch single orcamento servico
  const fetchOrcamentoServico = async (orcamentoServicoId: number) => {
    log('useOrcamentoServico', 'fetchOrcamentoServico', 'Fetching servico', orcamentoServicoId);

    try {
      const response = await httpService.get(
        `/index.php/orcamentos_servicos/carregaOrcamentoServico/${orcamentoServicoId}`,
        { responseContract: 'OrcamentosServicos_CarregaOrcamentoServicoResponse' }
      );

      if (response.success) {
        return response.dados;
      } else {
        throw new Error(response.error || 'Erro ao carregar serviço do orçamento');
      }
    } catch (error) {
      log('useOrcamentoServico', 'fetchOrcamentoServico', 'Error', error);
      throw error;
    }
  };

  // Save orcamento servico
  const saveOrcamentoServico = async (orcamentoServicoData: OrcamentosServicos_SalvaOrcamentoServicoInput) => {
    log('useOrcamentoServico', 'saveOrcamentoServico', 'Saving servico', orcamentoServicoData);

    try {
      validate('OrcamentosServicos_SalvaOrcamentoServicoInput', orcamentoServicoData);
      const response = await httpService.post('/index.php/orcamentos_servicos/salvaOrcamentoServico', orcamentoServicoData, {
        responseContract: 'OrcamentosServicos_SalvaOrcamentoServicoResponse',
      });

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao salvar serviço do orçamento');
      }
    } catch (error) {
      log('useOrcamentoServico', 'saveOrcamentoServico', 'Error', error);
      throw error;
    }
  };

  // Remove orcamento servico
  const removeOrcamentoServico = async (orcamentoServicoId: number) => {
    log('useOrcamentoServico', 'removeOrcamentoServico', 'Removing servico', orcamentoServicoId);

    try {
      const response = await httpService.get(`/index.php/orcamentos_servicos/removeOrcamentoServico/${orcamentoServicoId}`, {
        responseContract: 'OrcamentosServicos_RemoveorcamentoservicoResponse',
      });

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao remover serviço do orçamento');
      }
    } catch (error) {
      log('useOrcamentoServico', 'removeOrcamentoServico', 'Error', error);
      throw error;
    }
  };

  // Cancel orcamento servico
  const cancelOrcamentoServico = async (orcamentoServicoId: number) => {
    log('useOrcamentoServico', 'cancelOrcamentoServico', 'Canceling servico', orcamentoServicoId);

    try {
      const response = await httpService.get(`/index.php/orcamentos_servicos/cancelaOrcamentoServico/${orcamentoServicoId}`, {
        responseContract: 'OrcamentosServicos_CancelaorcamentoservicoResponse',
      });

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao cancelar serviço do orçamento');
      }
    } catch (error) {
      log('useOrcamentoServico', 'cancelOrcamentoServico', 'Error', error);
      throw error;
    }
  };

  // Reactivate orcamento servico
  const reactivateOrcamentoServico = async (orcamentoServicoId: number) => {
    log('useOrcamentoServico', 'reactivateOrcamentoServico', 'Reactivating servico', orcamentoServicoId);

    try {
      const response = await httpService.get(`/index.php/orcamentos_servicos/reativar_orcamento_servico/${orcamentoServicoId}`, {
        responseContract: 'OrcamentosServicos_ReativarOrcamentoServicoResponse',
      });

      if (response.success) {
        return response;
      } else {
        throw new Error(response.error || 'Erro ao reativar serviço do orçamento');
      }
    } catch (error) {
      log('useOrcamentoServico', 'reactivateOrcamentoServico', 'Error', error);
      throw error;
    }
  };

  // Check pre-cancellation
  const checkPreCancelamento = async (orcamentoServicoId: number) => {
    log('useOrcamentoServico', 'checkPreCancelamento', 'Checking pre-cancellation', orcamentoServicoId);

    try {
      const response = await httpService.get(`/index.php/orcamentos_servicos/verificaPreCancelamentoPorOrcamentoServicoID/${orcamentoServicoId}`, {
        responseContract: 'OrcamentosServicos_VerificaprecancelamentopororcamentoservicoidResponse',
      });

      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao verificar pré-cancelamento');
      }
    } catch (error) {
      log('useOrcamentoServico', 'checkPreCancelamento', 'Error', error);
      throw error;
    }
  };

  // Fetch available services
  const fetchServicos = async () => {
    log('useOrcamentoServico', 'fetchServicos', 'Fetching available services');
    
    try {
      const response = await httpService.get('/index.php/orcamentos_servicos/selectServicos', {
        responseContract: 'OrcamentosServicos_SelectserviciosResponse',
      });
      
      if (response.success) {
        return response.dados || [];
      } else {
        throw new Error(response.error || 'Erro ao buscar serviços disponíveis');
      }
    } catch (error) {
      log('useOrcamentoServico', 'fetchServicos', 'Error', error);
      throw error;
    }
  };

  // Fetch atendentes by franquia ID
  const fetchAtendentes = async (franquiaId: number) => {
    log('useOrcamentoServico', 'fetchAtendentes', 'Fetching atendentes for franquia', franquiaId);
    
    try {
      return permissionsService.getAtendentesSelectArrayByFranquiaID(franquiaId);
    } catch (error) {
      log('useOrcamentoServico', 'fetchAtendentes', 'Error', error);
      throw error;
    }
  };

  // Get tipos (hardcoded as per original implementation)
  const getTipos = () => {
    return [
      { ID: "1", Name: "Agendado no orçamento" },
      { ID: "2", Name: "Agendado no compromisso" }
    ];
  };

  // Get status (hardcoded as per original implementation)
  const getStatus = () => {
    return [
      { ID: "1", Name: "Aguardando aprovação do orçamento" },
      { ID: "2", Name: "Aguardando agendamento" },
      { ID: "3", Name: "Serviço agendado" },
      { ID: "4", Name: "Serviço realizado" },
      { ID: "5", Name: "Serviço cancelado" }
    ];
  };

  return {
    fetchOrcamentoServicos,
    fetchOrcamentoServico,
    saveOrcamentoServico,
    removeOrcamentoServico,
    cancelOrcamentoServico,
    reactivateOrcamentoServico,
    checkPreCancelamento,
    fetchServicos,
    fetchAtendentes,
    getTipos,
    getStatus
  };
}
